import axios from 'axios';
import { TimeOffRequest, PublicHoliday, TimeOffFormData, Event } from '../types';
import { openFilePreviewOrDownload } from '../utils/filePreview';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// API client for time off operations
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface TimeOffRequestFilters {
  startDate?: string;
  endDate?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
}

export interface PublicHolidayQuery {
  year?: number;
  fromYear?: number;
  toYear?: number;
  userOffice?: string;
}

export const timeOffService = {
  // Public holidays API integration
  async getPublicHolidays(
    yearOrOptions?: number | PublicHolidayQuery,
    userOffice?: string
  ): Promise<PublicHoliday[]> {
    try {
      const params: Record<string, string | number> = {};
      if (typeof yearOrOptions === 'number') {
        params.year = yearOrOptions;
        if (userOffice) params.office = userOffice;
      } else if (yearOrOptions) {
        if (yearOrOptions.userOffice) params.office = yearOrOptions.userOffice;
        if (yearOrOptions.fromYear != null && yearOrOptions.toYear != null) {
          params.from_year = yearOrOptions.fromYear;
          params.to_year = yearOrOptions.toYear;
        } else if (yearOrOptions.year != null) {
          params.year = yearOrOptions.year;
        }
      }
      const response = await api.get('/time-off/holidays', { params });
      return response.data.holidays || [];
    } catch (error) {
      console.error('Error fetching public holidays:', error);
      return [];
    }
  },

  // Time off requests management (database-backed)
  async getAllTimeOffRequests(filters?: TimeOffRequestFilters): Promise<TimeOffRequest[]> {
    try {
      const params: Record<string, string> = {};
      if (filters?.startDate) params.start_date = filters.startDate;
      if (filters?.endDate) params.end_date = filters.endDate;
      if (filters?.status) params.status = filters.status;
      const response = await api.get('/time-off/all-requests', { params });
      return response.data.requests || [];
    } catch (error) {
      console.error('Error fetching all time off requests:', error);
      return [];
    }
  },

  async getTimeOffRequestsByUser(userId: number): Promise<TimeOffRequest[]> {
    try {
      const response = await api.get('/time-off/requests');
      const allRequests = response.data.requests || [];
      return allRequests.filter((req: TimeOffRequest) => req.userId === userId);
    } catch (error) {
      console.error('Error fetching user time off requests:', error);
      return [];
    }
  },

  async getTimeOffRequestsByDate(date: string): Promise<TimeOffRequest[]> {
    const response = await api.get('/time-off/availability', {
      params: { date },
    });
    return response.data.requests || [];
  },

  async submitTimeOffRequest(
    userId: number,
    userName: string,
    formData: TimeOffFormData
  ): Promise<TimeOffRequest[]> {
    try {
      const payload = {
        dates: formData.dates,
        holidayType: formData.holidayType,
        hrCategory: formData.hrCategory,
        halfDayPeriod: formData.halfDayPeriod,
        notes: formData.notes,
        userId: formData.userId,
      };
      const response = await api.post('/time-off/requests', payload);
      return response.data.requests || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to submit time off request');
    }
  },

  async updateTimeOffRequest(id: string, updates: Partial<TimeOffRequest>): Promise<TimeOffRequest> {
    try {
      const response = await api.put(`/time-off/requests/${id}`, updates);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update time off request');
    }
  },

  async deleteTimeOffRequest(id: string): Promise<boolean> {
    try {
      await api.delete(`/time-off/requests/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting time off request:', error);
      return false;
    }
  },

  async bulkUpdateTimeOffRequests(requestIds: number[], status: 'approved' | 'rejected'): Promise<{ updated_count: number }> {
    try {
      const response = await api.put('/time-off/bulk-update', { request_ids: requestIds, status });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to bulk update time off requests');
    }
  },

  async getUserHolidayBalances(): Promise<{ year: number; balance: number; year_category: string; category: string }[]> {
    try {
      const response = await api.get('/time-off/balances');
      return response.data.balances || [];
    } catch (error) {
      console.error('Error fetching user holiday balances:', error);
      return [];
    }
  },

  async getLeaveSummaries(userIds: number[]): Promise<Record<string, { paid_leave: number; leave_awaiting_deduction: number }>> {
    if (userIds.length === 0) {
      return {};
    }
    try {
      const response = await api.get('/time-off/leave-summaries', {
        params: { user_ids: userIds.join(',') },
      });
      return response.data.summaries || {};
    } catch (error) {
      console.error('Error fetching leave summaries:', error);
      return {};
    }
  },

  async exportHolidayReport(startDate: string, endDate: string): Promise<void> {
    try {
      const response = await api.get('/hr/holiday-report/export', {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob',
      });

      openFilePreviewOrDownload(
        new Blob([response.data]),
        `holiday_report_${startDate}_to_${endDate}.pdf`,
        'application/pdf'
      );
    } catch (error) {
      console.error('Error exporting holiday report:', error);
      throw error;
    }
  },

  // Event management
  async getEvents(): Promise<Event[]> {
    try {
      const response = await api.get('/time-off/events');
      return response.data.events || [];
    } catch (error) {
      console.error('Error fetching events:', error);
      return [];
    }
  },

  async createEvent(eventData: {
    title: string;
    description?: string;
    event_date: string;
    affected_department?: number;
  }): Promise<Event> {
    try {
      const response = await api.post('/time-off/events', eventData);
      return response.data.event;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create event');
    }
  },

  async updateEvent(eventId: number, updates: Partial<{
    title: string;
    description?: string;
    event_date: string;
    affected_department?: number;
  }>): Promise<void> {
    try {
      await api.put(`/time-off/events/${eventId}`, updates);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update event');
    }
  },

  async deleteEvent(eventId: number): Promise<void> {
    try {
      await api.delete(`/time-off/events/${eventId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete event');
    }
  },

};

export default timeOffService;